import { OpenSansText } from "@Components/CustomText/OpenSansText"
import { Spacer } from "@Components/Space"
import { baseColors, baseFontFamily } from "@Constants/baseStyles"
import React, { useContext, useEffect, useState } from "react"
import { StyleSheet, TouchableOpacity, View } from "react-native"
import { Checkbox, RadioButton } from "react-native-paper"
import {
    ConditionWithNegation,
    handleSetMedicalCheckBoxRadioResponsesPayload,
    handleSetMedicalCheckBoxResponsesPayload
} from "./QuestionLogic"
import { ParentSurveyContent } from "./ParentSurveyContent"
import { canGotoNextAtom, surveyResponsesAtom } from "@Recoil/Atom/surveyAtom"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { currentDiagnosisAtom } from "@Recoil/Atom/DiagnosisAtom"
import { SALUTANCE } from "@Services/AxiosInstance"
import { MultilineInput } from "@Components/Input/MutlilineInput"
import { SurveyContext } from "@Providers/SurveyProvider"

export const QuestionCheckBoxRadio = (props) => {
    const {
        axisId,
        surveyId,
        responseTypeId,
        hasPrecision,
        question,
        checkBoxItems,
        userResponse,
        disabled,
        isParent,
        conditionToSkip,
        indexToSkip
    } = props

    const defaultResponse = {
        axisId: axisId,
        surveyId: surveyId,
        responseTypeId: responseTypeId,
        responseValue: "",
        hasPrecision: hasPrecision,
        precisionAnswer: ""
    }

    const [checked, setChecked] = useState([])
    const [checkedResCode, setCheckedResCode] = useState(null)
    const { setIndexToSkip } = useContext(SurveyContext)
    const [precision, setPrecision] = useState("")
    const [responseGlobal, setResponseGlobal] = useRecoilState(surveyResponsesAtom)
    const currentEducationalDiagnosis = useRecoilValue(currentDiagnosisAtom)
    const setCanGoToNext = useSetRecoilState(canGotoNextAtom)

    useEffect(() => {
        setCanGoToNext(true)
    }, [])

    useEffect(() => {
        setChecked(() => {
            if (userResponse?.id > 0 && currentEducationalDiagnosis?.id > 0) {
                const filtered = userResponse?.responseData?.survey?.responseItems?.filter((item) => item?.id > 0) ?? []
                const patientResIds = filtered?.map((item) => item.id)
                return patientResIds ?? []
            } else {
                if (responseGlobal?.[surveyId]) {
                    const ids = responseGlobal?.[surveyId]?.responseIds ?? []
                    const lastResponse = checkBoxItems?.find((item) => item?.proposalAnswer?.id === ids?.[0])
                    setCheckedResCode(
                        (ConditionWithNegation(conditionToSkip) ? "!" : "") + lastResponse?.proposalAnswer?.code
                    )
                    return ids ?? []
                }
                return []
            }
        })
    }, [userResponse, responseGlobal, checkBoxItems, conditionToSkip])

    const handleCheck = (responseId) =>
        handleSetMedicalCheckBoxRadioResponsesPayload({
            defaultResponse,
            surveyId,
            responseId,
            setResponseGlobal,
            setChecked
        })

    const handleChangePrecision = (textValue) => {
        setPrecision(textValue)
        setResponseGlobal((prev) => {
            if (responseGlobal[surveyId]) {
                return {
                    ...prev,
                    [surveyId]: { ...responseGlobal[surveyId], precisionAnswer: textValue }
                }
            } else {
                return {
                    ...prev,
                    [surveyId]: { ...defaultResponse, precisionAnswer: textValue }
                }
            }
        })
    }

    useEffect(() => {
        if (!responseGlobal?.[surveyId] && checked?.length === 0) {
            setResponseGlobal((prev) => {
                return {
                    ...prev,
                    [surveyId]: { ...defaultResponse, responseIds: [] }
                }
            })
        }
    }, [checked, responseGlobal])

    useEffect(() => {
        if (SALUTANCE && conditionToSkip && checkedResCode) {
            setIndexToSkip(() => {
                if (ConditionWithNegation(conditionToSkip)) {
                    if (checkedResCode !== conditionToSkip) {
                        return indexToSkip
                    }
                } else {
                    if (checkedResCode === conditionToSkip) {
                        return indexToSkip
                    }
                }
                return 0
            })
        } else {
            setIndexToSkip(0)
        }
    }, [checkedResCode, SALUTANCE, conditionToSkip, indexToSkip])

    const HandleSetValue = (item) => {
        setCheckedResCode((ConditionWithNegation(conditionToSkip) ? "!" : "") + item?.proposalAnswer?.code)
        handleCheck(item?.proposalAnswer?.id)
    }

    if (isParent) {
        return <ParentSurveyContent question={question} />
    }

    return (
        <View style={{ marginBottom: 50, padding: 20 }}>
            <OpenSansText defaultFontFamily={baseFontFamily.OpenSansRegular} color={baseColors.orange.primary}>
                {SALUTANCE ? "QUESTION : " : `QUESTION N° `}
            </OpenSansText>

            <OpenSansText defaultFontFamily={baseFontFamily.OpenSansRegular} style={{ marginTop: 10 }} fontSize={14}>
                {question ?? ""}
            </OpenSansText>

            <Spacer height={20} />

            <View style={{}}>
                {checkBoxItems?.map((item) => (
                    <TouchableOpacity
                        onPress={() => HandleSetValue(item)}
                        style={styles.checkBox}
                        key={item?.proposalAnswer?.id}
                    >
                        <RadioButton.Android
                            disabled={disabled}
                            color={baseColors.green.primary}
                            onPress={() => HandleSetValue(item)}
                            status={checked?.includes(item?.proposalAnswer?.id) ? "checked" : "unchecked"}
                        />

                        <View style={{ flex: 1, marginLeft: 5 }}>
                            <OpenSansText
                                defaultFontFamily={baseFontFamily.OpenSansSemiBold}
                                fontSize={12}
                                style={styles.checkBoxLabelGreen}
                            >
                                {item?.proposalAnswer?.name}
                            </OpenSansText>
                        </View>
                    </TouchableOpacity>
                ))}
            </View>

            {Boolean(hasPrecision) && (
                <>
                    <Spacer height={20} />
                    <OpenSansText fontSize={14}>
                        {question?.precision ?? "Apportez des précisions si vous le souhaitez"} :
                    </OpenSansText>
                    <Spacer height={20} />
                    <MultilineInput
                        disabled={disabled}
                        height={80}
                        value={precision}
                        onChange={(text) => handleChangePrecision(text)}
                    />
                </>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    checkBox: { flexDirection: "row", alignItems: "center", marginBottom: 20 }
})
